import React, { useEffect, useState } from 'react';
import { Button, Card, H3, Text, Icon, Spinner, Callout, Dialog, Classes, Divider, Intent, InputGroup, FormGroup } from '@blueprintjs/core';
import { slackApi } from '../../api/slack';
import { notificationService } from '../../services/notification.service';
import { useAppStore } from '../../stores/UnifiedStoreProvider';

interface SlackIntegrationProps {
  organizationId?: string;
  onConnectionChange?: (connected: boolean) => void;
}

/**
 * Slack Integration component that handles OAuth connection and workspace management
 */
const SlackIntegration: React.FC<SlackIntegrationProps> = ({ organizationId, onConnectionChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState<boolean>(true);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [showWorkspaceDetails, setShowWorkspaceDetails] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const [workspaceUsers, setWorkspaceUsers] = useState<any[]>([]);
  const [workspaceChannels, setWorkspaceChannels] = useState<any[]>([]);
  const [showNotificationTest, setShowNotificationTest] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [notificationHealth, setNotificationHealth] = useState<any>(null);

  // Get current user for testing
  const appStore = useAppStore();
  const { auth } = appStore;
  const currentUser = auth?.user;

  // Check connection status on mount and when URL changes
  useEffect(() => {
    checkConnectionStatus();
    checkNotificationHealth();
  }, [organizationId]);

  // Handle URL parameters from OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connectionSuccess = urlParams.get('connected');
    const teamId = urlParams.get('team_id');
    const teamName = urlParams.get('team_name');
    const error = urlParams.get('error');

    if (connectionSuccess === '1' && teamId) {
      setConnected(true);
      setError(null);
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      checkConnectionStatus(); // Refresh workspace list
    } else if (error) {
      setError(`Connection failed: ${decodeURIComponent(error)}`);
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Notify parent of connection changes
  useEffect(() => {
    onConnectionChange?.(connected);
  }, [connected, onConnectionChange]);

  const checkConnectionStatus = async () => {
    setCheckingStatus(true);
    setError(null);
    try {
      const data = await slackApi.getConnectionStatus(organizationId);
      setConnected(data.connected || false);
      setWorkspaces(data.workspaces || []);
      setBackendAvailable(true);
    } catch (err: any) {
      console.warn('Failed to check Slack connection status:', err);
      setConnected(false);
      setWorkspaces([]);
      
      // Check if this is a backend connectivity issue
      if (err.message?.includes('Backend server unavailable') || 
          err.message?.includes('Cannot connect to backend server')) {
        setBackendAvailable(false);
        setError('Backend server is not available. Please ensure the forethought-backend is running.');
      } else {
        setError(`Connection check failed: ${err.message}`);
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const checkNotificationHealth = async () => {
    try {
      const health = await notificationService.getHealth();
      setNotificationHealth(health);
    } catch (error) {
      console.error('Failed to check notification health:', error);
    }
  };

  const handleConnect = async () => {
    if (!organizationId) {
      setError('Organization ID is required for Slack integration');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { url } = await slackApi.getAuthUrl(organizationId);
      // Redirect to Slack OAuth
      window.location.href = url;
    } catch (err: any) {
      console.error('Failed to get Slack auth URL:', err);
      setError(`Failed to initiate connection: ${err.message}`);
      setLoading(false);
    }
  };

  const handleDisconnect = async (teamId: string) => {
    setLoading(true);
    setError(null);
    try {
      await slackApi.disconnectWorkspace(teamId);
      setConnected(false);
      setWorkspaces([]);
      await checkConnectionStatus(); // Refresh status
    } catch (err: any) {
      console.error('Failed to disconnect Slack workspace:', err);
      setError(`Failed to disconnect: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (workspace: any) => {
    try {
      const result = await slackApi.testConnection(workspace.teamId);
      if (result.success) {
        setError(null);
        setSelectedWorkspace({ ...workspace, testResult: result });
        setShowWorkspaceDetails(true);
        
        // Load workspace users and channels
        const [usersData, channelsData] = await Promise.all([
          slackApi.getWorkspaceUsers(workspace.teamId),
          slackApi.getWorkspaceChannels(workspace.teamId)
        ]);
        
        setWorkspaceUsers(usersData.users || []);
        setWorkspaceChannels(channelsData.channels || []);
      } else {
        setError(`Connection test failed: ${result.error}`);
      }
    } catch (err: any) {
      setError(`Test failed: ${err.message}`);
    }
  };

  const handleTestNotification = async () => {
    if (!currentUser || !testMessage.trim()) return;
    
    try {
      const success = await notificationService.sendCommentMentionNotification({
        commentId: 'test-' + Date.now(),
        nodeId: 'test-node',
        nodeLabel: 'Test Node',
        authorId: currentUser.id,
        authorName: 'Test User',
        content: testMessage,
        mentions: [{ type: 'person', id: currentUser.id, label: 'Test User' }],
        createdAt: Date.now()
      });
      
      if (success) {
        setError(null);
        alert('Test notification sent successfully!');
      } else {
        setError('Failed to send test notification');
      }
    } catch (error) {
      setError(`Test notification failed: ${error}`);
    }
  };

  const handleRefresh = () => {
    checkConnectionStatus();
    checkNotificationHealth();
  };

  if (checkingStatus) {
    return (
      <Card elevation={2} style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner size={16} />
          <span>Checking Slack connection...</span>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card elevation={2} style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Slack logo SVG */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.522 2.521 2.528 2.528 0 0 1-2.522-2.521V2.522A2.528 2.528 0 0 1 15.166 0a2.528 2.528 0 0 1 2.522 2.522v6.312zM15.166 18.956a2.528 2.528 0 0 1 2.522 2.522A2.528 2.528 0 0 1 15.166 24a2.528 2.528 0 0 1-2.522-2.522v-2.522h2.522zM15.166 17.688a2.528 2.528 0 0 1-2.522-2.523 2.528 2.528 0 0 1 2.522-2.521h6.312A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.312z" fill="#E01E5A"/>
            </svg>
            <h3 style={{ margin: 0 }}>Slack Integration</h3>
          </div>
          <Button 
            minimal 
            icon="refresh" 
            onClick={handleRefresh}
            title="Refresh connection status"
          />
        </div>

        <p style={{ marginBottom: 16, color: '#5c7080' }}>
          Connect your organization's Slack workspace to enable mention notifications and comment synchronization.
        </p>

        {error && (
          <Callout intent={Intent.DANGER} icon="error" style={{ marginBottom: 16 }}>
            {error}
            {!backendAvailable && (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <strong>To start the backend:</strong>
                <br />• Navigate to the forethought-backend directory
                <br />• Run: <code>npm start</code> or <code>yarn start</code>
                <br />• Ensure it's running on port 3000
              </div>
            )}
          </Callout>
        )}

        {!backendAvailable && !error && (
          <Callout intent={Intent.WARNING} icon="warning-sign" style={{ marginBottom: 16 }}>
            <strong>Backend Unavailable</strong>
            <div style={{ marginTop: 8, fontSize: 13 }}>
              The forethought-backend server is not running. Slack integration requires the backend to be available.
            </div>
          </Callout>
        )}

        {connected ? (
          <>
            <Callout intent={Intent.SUCCESS} icon="tick" style={{ marginBottom: 16 }}>
              <strong>Slack workspace connected successfully!</strong>
              <div style={{ marginTop: 8, fontSize: 13 }}>
                {workspaces.length > 0 
                  ? `Connected to ${workspaces.length} workspace${workspaces.length > 1 ? 's' : ''}`
                  : 'Your Slack workspace is connected to Forethought'
                }. You can now receive mention notifications and sync comments.
              </div>
            </Callout>
            
            <Divider style={{ margin: '16px 0' }} />
            
            <div style={{ marginBottom: 16 }}>
              <Text style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, display: 'block' }}>
                Connected Workspaces ({workspaces.length}):
              </Text>
              {workspaces.map((workspace) => (
                <div key={workspace.teamId} style={{ 
                  marginBottom: 12,
                  padding: 16, 
                  backgroundColor: 'rgba(138, 155, 168, 0.1)', 
                  borderRadius: 6,
                  border: '1px solid rgba(138, 155, 168, 0.2)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <Text style={{ fontWeight: 600, fontSize: 14 }}>{workspace.teamName}</Text>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#5c7080' }}>
                      Connected: {new Date(workspace.connectedAt).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      icon="eye-open"
                      small
                      onClick={() => handleTestConnection(workspace)}
                    >
                      Details
                    </Button>
                    <Button
                      icon="th-disconnect"
                      intent="danger"
                      small
                      loading={loading}
                      onClick={() => handleDisconnect(workspace.teamId)}
                    >
                      Disconnect
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Notification Testing Section */}
            <Divider style={{ margin: '16px 0' }} />
            
            <div style={{ marginBottom: 16 }}>
              <Text style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, display: 'block' }}>
                🔔 Mention Notifications
              </Text>
              
              {notificationHealth && (
                <div style={{ marginBottom: 12, padding: 12, backgroundColor: 'rgba(138, 155, 168, 0.05)', borderRadius: 4 }}>
                  <Text style={{ fontSize: 12, color: '#5c7080' }}>
                    <strong>Status:</strong> {notificationHealth.status} | 
                    <strong> User Mappings:</strong> {notificationHealth.mappings} | 
                    <strong> Workspaces:</strong> {notificationHealth.workspaces?.length || 0}
                  </Text>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button
                  icon="chat"
                  onClick={() => setShowNotificationTest(true)}
                  small
                >
                  Test Notifications
                </Button>
                {workspaces.length > 0 && (
                  <Button
                    icon="people"
                    onClick={async () => {
                      const mappedCount = await notificationService.autoMapUsers(workspaces[0].teamId);
                      alert(`Auto-mapped ${mappedCount} users by email`);
                      checkNotificationHealth();
                    }}
                    small
                  >
                    Auto-Map Users
                  </Button>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button 
                intent="primary" 
                icon="plus" 
                onClick={handleConnect}
                disabled={loading || !organizationId}
              >
                Connect Another Workspace
              </Button>
              <Button
                icon="refresh"
                onClick={handleRefresh}
              >
                Refresh Status
              </Button>
            </div>
          </>
        ) : (
          <div>
            <Callout intent={Intent.WARNING} icon="warning-sign" style={{ marginBottom: 16 }}>
              Slack is not connected. Connect your workspace to enable notifications and comment sync.
            </Callout>
            
            <Button
              intent="primary"
              icon="link"
              onClick={handleConnect}
              disabled={loading || !organizationId}
              style={{ marginRight: 10 }}
            >
              {loading ? <Spinner size={16} /> : 'Connect Slack Workspace'}
            </Button>
            
            <Button 
              icon="help" 
              onClick={() => window.open('https://api.slack.com/authentication/basics', '_blank')}
            >
              Learn More
            </Button>
            
            {!organizationId && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#5c7080' }}>
                Organization ID is required for Slack integration
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Workspace Details Dialog */}
      <Dialog
        isOpen={showWorkspaceDetails}
        onClose={() => setShowWorkspaceDetails(false)}
        title={`Workspace: ${selectedWorkspace?.teamName}`}
        className={Classes.DIALOG_BODY}
        style={{ width: '600px' }}
      >
        <div className={Classes.DIALOG_BODY}>
          {selectedWorkspace?.testResult && (
            <div style={{ marginBottom: '16px' }}>
              <H3>Connection Test Results</H3>
              <Callout intent="success">
                <Text>Connection test successful!</Text>
                <div style={{ marginTop: '8px' }}>
                  <Text><strong>Bot User ID:</strong> {selectedWorkspace.testResult.botInfo?.userId}</Text><br />
                  <Text><strong>Team ID:</strong> {selectedWorkspace.testResult.botInfo?.teamId}</Text><br />
                  <Text><strong>Team Name:</strong> {selectedWorkspace.testResult.botInfo?.teamName}</Text>
                </div>
              </Callout>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <H3>Users ({workspaceUsers.length})</H3>
            <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #e1e5e9', borderRadius: '4px', padding: '8px' }}>
              {workspaceUsers.map((user) => (
                <div key={user.id} style={{ padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <Text><strong>{user.real_name || user.name}</strong> (@{user.name})</Text>
                  {user.profile?.email && <Text style={{ fontSize: '12px', color: '#666' }}>{user.profile.email}</Text>}
                </div>
              ))}
            </div>
          </div>

          <div>
            <H3>Channels ({workspaceChannels.length})</H3>
            <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #e1e5e9', borderRadius: '4px', padding: '8px' }}>
              {workspaceChannels.map((channel) => (
                <div key={channel.id} style={{ padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <Text><strong>#{channel.name}</strong> {channel.is_private && '(Private)'}</Text>
                  {channel.purpose?.value && <Text style={{ fontSize: '12px', color: '#666' }}>{channel.purpose.value}</Text>}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className={Classes.DIALOG_FOOTER}>
          <Button onClick={() => setShowWorkspaceDetails(false)}>Close</Button>
        </div>
      </Dialog>

      {/* Notification Test Dialog */}
      <Dialog
        isOpen={showNotificationTest}
        onClose={() => setShowNotificationTest(false)}
        title="Test Mention Notification"
        style={{ width: '500px' }}
      >
        <div className={Classes.DIALOG_BODY}>
          <FormGroup
            label="Test Message"
            helperText="Enter a message to test the notification system"
          >
            <InputGroup
              value={testMessage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestMessage(e.target.value)}
              placeholder="Test message with @mention..."
            />
          </FormGroup>
        </div>
        
        <div className={Classes.DIALOG_FOOTER}>
          <Button onClick={() => setShowNotificationTest(false)}>Cancel</Button>
          <Button 
            intent="primary" 
            onClick={handleTestNotification}
            disabled={!testMessage.trim()}
          >
            Send Test Notification
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default SlackIntegration; 